/**
 * Trade rules of each consolidation (TRADE-CONS step 5a, migration 0134; backend
 * `routers/trade_rules.py`): what the optimizer follows. Read by `portfolios.read`; changed by
 * `portfolios.trade_rules` — direct and audited, no maker-checker, as the other set-up tabs.
 */
import { api } from './client';
import type { UUID } from './types';

export type GoalKey = 'cross_bank' | 'transfers' | 'trades' | 'volume' | 'gains';
export type RankingPreset = 'keep_apart' | 'minimal' | 'tax_aware' | 'custom';

export interface MemberRules {
  link_id: UUID; ref: string; label: string;
  /** the portfolio's reference currency (the minimum cash is in it); null = no positions yet */
  ref_ccy: string | null;
  min_cash: string; wrapper: string | null;
}
export interface TradeRules {
  consolidation_id: UUID; consolidation_name: string; agreement_id: UUID; agreement_label: string;
  /** false = nothing saved yet: the defaults apply */
  saved: boolean;
  tolerance_bps: string; preset: RankingPreset; preset_label: string; ranking: GoalKey[];
  members: MemberRules[];
  updated_at: string | null; updated_by_name: string | null;
}
export interface TradeRulesList {
  items: TradeRules[]; total: number;
  goals: Record<GoalKey, string>; first: GoalKey;
  presets: Record<Exclude<RankingPreset, 'custom'>, GoalKey[]>; preset_labels: Record<RankingPreset, string>;
  defaults: { tolerance_bps: string; preset: RankingPreset };
}
export interface TradeRulesIn {
  tolerance_bps: string; preset: RankingPreset; ranking: GoalKey[];
  members: { link_id: UUID; min_cash: string; wrapper: string | null }[];
}

/** The goal order with "Closest to the target" in its place: just before the first money goal
 *  (traded value or gains) — Pedro 2026-11-01, "Yes, before them" (as `trade_optimizer.with_target`). */
export const TARGET_GOAL = 'target';
export function withTarget(ranking: string[]): string[] {
  const out = [...ranking];
  const i = out.findIndex((g) => g === 'volume' || g === 'gains');
  out.splice(i < 0 ? out.length : i, 0, TARGET_GOAL);
  return out;
}
export const TARGET_LABEL = 'Closest to the target';

export const tradeRulesApi = {
  list: (agreementId?: UUID) =>
    api.get<TradeRulesList>(`/trade-rules${agreementId ? `?agreement_id=${encodeURIComponent(agreementId)}` : ''}`),
  get: (consolidationId: UUID) => api.get<TradeRules>(`/trade-rules/${consolidationId}`),
  save: (consolidationId: UUID, body: TradeRulesIn) => api.put<TradeRules>(`/trade-rules/${consolidationId}`, body),
};
